import { useTranslation } from 'react-i18next';
import { Badge } from '@monksflow/monks-ui';
import { Check, ChevronRight, Plus } from 'lucide-react';
import {
  IQ_DATA_SOURCE_CATEGORIES,
  IQ_DATA_SOURCE_DESCRIPTIONS,
  IQ_DATA_SOURCE_LABELS,
  IQ_DATA_SOURCE_MARKS,
  type IqDataSource,
} from '@/entities/iq-data-source';

interface DataSourceListProps {
  sources: IqDataSource[];
  attachedSources: ReadonlySet<IqDataSource>;
  onSelect: (source: IqDataSource) => void;
}

export const DataSourceList = ({ sources, attachedSources, onSelect }: DataSourceListProps) => {
  const { t } = useTranslation('analysis');

  if (sources.length === 0) {
    return <p className="text-sm text-foreground-alt">{t('dataSourcesPanel.empty')}</p>;
  }

  return (
    <section className="flex flex-col gap-3.5">
      <div className="flex flex-col">
        <span className="text-base font-medium">{t('dataSourcesPanel.availableTitle')}</span>
        <span className="text-sm text-foreground-alt">
          {t('dataSourcesPanel.availableSubtitle')}
        </span>
      </div>

      <div className="flex flex-col gap-3">
        {sources.map((source) => {
          const attached = attachedSources.has(source);
          return (
            <button
              key={source}
              type="button"
              onClick={() => onSelect(source)}
              className="flex flex-col gap-3 rounded-xl border border-border-light bg-background p-4 text-left transition-colors hover:border-primary"
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className="grid size-9 shrink-0 place-items-center rounded-lg border border-border-light bg-surface text-base font-medium"
                >
                  {IQ_DATA_SOURCE_MARKS[source]}
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-base font-medium">{IQ_DATA_SOURCE_LABELS[source]}</span>
                  <span className="text-xs text-foreground-alt">
                    {IQ_DATA_SOURCE_CATEGORIES[source]}
                  </span>
                </div>
                <Badge
                  variant="ghost"
                  roundness="default"
                  className={
                    attached
                      ? 'border-green-600/30 bg-green-50 px-2.5 text-xs font-medium text-green-700 dark:bg-green-950/30 dark:text-green-400'
                      : 'bg-accent px-2.5 text-xs font-medium text-primary'
                  }
                >
                  {attached ? <Check className="mr-1 size-3" /> : <Plus className="mr-1 size-3" />}
                  {attached
                    ? t('dataSourcesPanel.badgeAttached')
                    : t('dataSourcesPanel.badgeAvailable')}
                </Badge>
                <ChevronRight className="size-4 shrink-0 text-foreground-alt" aria-hidden />
              </div>
              <p className="text-sm text-foreground-alt">{IQ_DATA_SOURCE_DESCRIPTIONS[source]}</p>
            </button>
          );
        })}
      </div>
    </section>
  );
};

DataSourceList.displayName = 'DataSourceList';
